import React, { useEffect, useState } from "react";
import UserModel from "app/models/UserModel";
import CoachCard from "../CoachCard";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import useCoachBooking from "app/routes/Sessions/hooks/useCoachBooking";
import ParticipantPickerModal from "app/routes/Sessions/ParticipantPickerModal";
import { useBookingParticipants } from "app/routes/Sessions/useBookingParticipants";

interface Props {
  coaches: UserModel[];
  coachStats: { [coachId: number]: number };
}

const MyCoachesList = ({ coaches, coachStats }: Props) => {
  const { t } = useTranslation();
  const { startBooking } = useCoachBooking();
  // CN-1323: a head of family says who the session is for before the calendar
  // opens, exactly as the My Bookings button and the coach's profile do.
  const { candidates, hasFamily, refetch } = useBookingParticipants();
  const [pendingCoachId, setPendingCoachId] = useState<string | null>(null);

  /**
   * Which of these coaches can be booked in the app. Their card's button says so,
   * so it is worth one request: the roster is the same `/coach_profiles` the
   * booking flow reads, and a coach missing from it has no calendar to open.
   */
  const [bookableCoachIds, setBookableCoachIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    let active = true;

    schedulingFetchGet("/coach_profiles")
      .then((profiles: any) => {
        if (!active) {
          return;
        }
        setBookableCoachIds(
          new Set(
            (profiles || [])
              .map((profile: any) => String(profile?.externalId || ""))
              .filter(Boolean),
          ),
        );
      })
      // An athlete with no scheduling profile has no bookable coaches, which is
      // what an empty set already says.
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, []);

  const onBookSession = (user: UserModel) => {
    if (hasFamily) {
      setPendingCoachId(String(user?.id));
      return;
    }
    startBooking(String(user?.id), null);
  };

  const onConfirmParticipant = (participantAthleteId: string | null) => {
    const coachId = pendingCoachId;
    setPendingCoachId(null);
    if (coachId) {
      startBooking(coachId, participantAthleteId);
    }
  };

  if (coaches.length === 0) {
    return (
      <div className={styles.empty_container}>
        <p className={styles.empty_text}>{t("No Coaches")}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ParticipantPickerModal
        isOpen={pendingCoachId !== null}
        candidates={candidates}
        onClose={() => setPendingCoachId(null)}
        onConfirm={onConfirmParticipant}
        onMemberAdded={refetch}
      />
      {coaches?.map((user: UserModel) => {
        const productCount = coachStats[user?.id] || 0;
        return (
          <CoachCard
            key={user?.id}
            user={user}
            productCount={productCount}
            bookable={bookableCoachIds.has(String(user?.id))}
            onBookSession={onBookSession}
          />
        );
      })}
    </div>
  );
};

export default MyCoachesList;
